import { useState, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { useLocale } from "../../context/LanguageContext";

interface RefreshButtonProps {
  onRefresh: () => Promise<unknown>;
  loading?: boolean;
}

/**
 * Labeled refresh button with guaranteed visual feedback (min 600ms spinner).
 */
export function RefreshButton({ onRefresh, loading = false }: RefreshButtonProps) {
  const { t } = useLocale();
  const [spinning, setSpinning] = useState(false);
  const isActive = loading || spinning;

  const handleClick = useCallback(async () => {
    setSpinning(true);
    try {
      await onRefresh();
    } finally {
      setTimeout(() => setSpinning(false), 600);
    }
  }, [onRefresh]);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isActive}
      className="inline-flex h-9 items-center gap-2 rounded-md border border-border px-4 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
    >
      <RefreshCw className={`h-4 w-4 ${isActive ? "animate-spin" : ""}`} />
      {isActive ? t("dashboard.refreshing") : t("common.refresh")}
    </button>
  );
}

/**
 * Icon-only refresh button with guaranteed visual feedback (min 600ms spinner).
 */
export function RefreshIconButton({ onRefresh, loading = false }: RefreshButtonProps) {
  const { t } = useLocale();
  const [spinning, setSpinning] = useState(false);
  const isActive = loading || spinning;

  const handleClick = useCallback(async () => {
    setSpinning(true);
    try {
      await onRefresh();
    } finally {
      setTimeout(() => setSpinning(false), 600);
    }
  }, [onRefresh]);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isActive}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted disabled:opacity-50"
      title={t("common.refresh")}
    >
      <RefreshCw className={`h-4 w-4 ${isActive ? "animate-spin" : ""}`} />
    </button>
  );
}
